import React, { useState, useEffect } from 'react';
import { emailRegex, FormState, passwordRegex } from '@opu/utils';
import { useHistory, useLocation } from 'react-router-dom';
import AxiosInstance from '../../../helpers/AxiosInstance';
import { auth } from '../../../firebase-init';
import firebase from 'firebase/app';
import { useTranslation } from 'react-i18next';

const useForm = () => {
  let history = useHistory();
  const [loginInputs, setLoginInputs] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState(null);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [authMethod, setAuthMethod] = useState('email'); // 'email' or 'bac'
  
  // Separate state for BAC matricule fields
  const [bacMatricule, setBacMatricule] = useState('');
  const [birthDate, setBirthDate] = useState('');

  const { t, i18n } = useTranslation();

  const bacMatriculeLogin = async () => {
    console.log('🎓 BAC MATRICULE LOGIN FUNCTION CALLED');
    console.log('🎓 BAC Matricule value:', bacMatricule);
    console.log('🎓 Birth Date value:', birthDate);
    console.log('🎓 Setting loading to true...');
    setLoading(true);
    setErrors('');

    try {
      console.log('🌐 Preparing API request...');
      console.log('🌐 Request payload:', {
        identifier: bacMatricule,
        password: birthDate
      });
      
      console.log('🌐 Sending POST request to /auth/login...');
      const response = await AxiosInstance.post('/auth/login', {
        identifier: bacMatricule,
        password: birthDate
      });

      console.log('🌐 API response received:', response);
      console.log('🌐 Response status:', response.status);
      console.log('🌐 Response data:', response.data);
      console.log('🌐 Response statusText:', response.statusText);
      console.log('🌐 Response headers:', response.headers);
      console.log('🌐 Full response object keys:', Object.keys(response));
      console.log('🌐 Response.data keys:', response.data ? Object.keys(response.data) : 'No data');
      console.log('🌐 Response message:', response.message);
      console.log('🌐 Response.data.message:', response.data?.message);
      console.log('🌐 Checking firebase_uid in response.data:', response.data?.firebase_uid);
      
      // Check for success - look for either the message or the presence of firebase_uid
      // If response.data has firebase_uid, login was successful
      const hasMessage = response.message === "AuthenticatedUserFetchedSuccessfully" ||
                         response.data?.message === "AuthenticatedUserFetchedSuccessfully";
      const hasUserData = response.data?.firebase_uid || response.data?.id;
      const isSuccessful = response.status === 201 || hasMessage || hasUserData;
      
      console.log('🌐 Has message?', hasMessage);
      console.log('🌐 Has user data?', hasUserData);
      console.log('🌐 Is login successful?', isSuccessful);
      
      if (isSuccessful) {
        console.log('✅ LOGIN SUCCESSFUL! Setting up Firebase authentication...');
        
        // Get the user data from the response - API returns { message, data }
        const userData = response.data?.data || response.data;
        console.log('🔍 User data from API:', userData);
        console.log('🔍 requiresFirebaseAuth?', userData.requiresFirebaseAuth);
        
        if (userData.firebase_uid) {
          console.log('🔍 User has Firebase UID, signing in with Firebase...');
          
          // Check if custom password is being used
          let firebaseEmail;
          let firebasePassword;
          
          if (userData.requiresFirebaseAuth && userData.email) {
            // User has custom password, use provided email and entered password
            firebaseEmail = userData.email;
            firebasePassword = birthDate; // The entered password
            console.log('🔍 User has custom password, using email login:', firebaseEmail);
          } else {
            // User has birth_date password, use temp email and birth date
            firebaseEmail = `${userData.bac_matricule}@temp.opu.local`;
            firebasePassword = birthDate;
            console.log('🔍 User has birth_date password, using temp email:', firebaseEmail);
          }
          
          try {
            console.log('🔍 Attempting Firebase sign-in with:', { firebaseEmail });
            const firebaseResult = await auth.signInWithEmailAndPassword(firebaseEmail, firebasePassword);
            console.log('✅ Firebase sign-in successful:', firebaseResult.user.uid);
            console.log('🔍 Firebase user object:', firebaseResult.user);
            
            // Set the user state to trigger the authentication flow
            console.log('🔍 Setting user state...');
            setUser(firebaseResult.user);
            
            // Also call getUserPersonalInformation directly like the email login does
            console.log('🔍 Calling getUserPersonalInformation directly...');
            getUserPersonalInformation(firebaseResult.user);
            
            console.log('✅ Firebase authentication set up, user state updated');
          } catch (firebaseError) {
            console.error('❌ Firebase sign-in failed:', firebaseError);
            console.error('❌ Firebase error code:', firebaseError.code);
            console.error('❌ Firebase error message:', firebaseError.message);
            
            // Show appropriate error message
            if (firebaseError.code === 'auth/wrong-password') {
              setErrors(t('login.errors.invalid-password'));
            } else if (firebaseError.code === 'auth/user-not-found') {
              setErrors(t('login.errors.user-not-found'));
            } else {
              setErrors(t('login.errors.authentication-error'));
            }
            setLoading(false);
          }
        } else {
          console.log('⚠️ User has no Firebase UID, redirecting directly');
          history.push('/');
        }
      } else {
        console.log('⚠️ Unexpected response status:', response.status);
        console.log('⚠️ Response message:', response.data?.message);
        console.log('⚠️ Full response structure:', Object.keys(response));
        
        // Handle "AuthenticatedUserNotFound" case
        if (response.data?.message === "AuthenticatedUserNotFound") {
          console.log('❌ User not found with provided credentials');
          setErrors(t('login.errors.authentication-error'));
        }
      }
    } catch (error) {
      console.error('❌ BAC MATRICULE LOGIN ERROR:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      
      if (error.response?.data?.message) {
        if (Array.isArray(error.response.data.message)) {
          console.log('❌ Setting error message (array):', error.response.data.message.join(', '));
          setErrors(error.response.data.message.join(', '));
        } else {
          console.log('❌ Setting error message (string):', error.response.data.message);
          setErrors(error.response.data.message);
        }
      } else {
        console.log('❌ Setting generic error message');
        setErrors(t('login.errors.authentication-error'));
      }
    } finally {
      console.log('🏁 Setting loading to false...');
      setLoading(false);
    }
  };

  const firebaseLogin = async () => {
    setLoading(true);
    setErrors('');

    auth
      .signInWithEmailAndPassword(formState.inputValues.email, formState.inputValues.password)
      .then(async (result) => {
        getUserPersonalInformation(result.user);
        setLoading(false);
      })
      .catch((error) => {
        switch (error.code) {
          case 'auth/user-not-found':
            setErrors(t('login.errors.user-not-found'));
            break;

          case 'auth/wrong-password':
            handleErrorsServer(['ERROR_PASSWORD_INVALID']);
            break;

          case 'auth/invalid-email':
            handleErrorsServer(['ERROR_EMAIL_PATTERN']);
            break;

          default:
            setErrors(t('login.errors.authentication-error'));
            break;
        }

        setLoading(false);
      });
  };

  const firebaseProviderLogin = async (provider_) => {
    let provider;
    setLoading(true);
    switch (provider_) {
      case 'facebook':
        provider = new firebase.auth.FacebookAuthProvider();
        provider.addScope('email');

        break;

      case 'google':
        provider = new firebase.auth.GoogleAuthProvider();
        provider.addScope('email');
        break;

      default:
        setErrors(t('login.errors.authentication-error'));

        break;
    }

    auth
      .signInWithPopup(provider)
      .then(async function (result) {
        if (result.additionalUserInfo.isNewUser) {
          history.push('/personal-information');
        } else {
          getUserPersonalInformation(result.user);
        }
      })
      .catch((error) => {
        setLoading(false);
        if (error.code === 'auth/popup-closed-by-user') setErrors(t('ERROR_AUTHENTICATION_CANCELLED'));
        setErrors(t('login.errors.authentication-error'));
      });
  };

  useEffect(() => {
    if (user && user.uid) {
      getUserPersonalInformation(user);
    }
  }, [user]);

  const getUserPersonalInformation = (user) => {
    console.log('🔍 getUserPersonalInformation called with user:', user);
    console.log('🔍 User UID:', user.uid);
    
    //http request to the api and see if personal information has been set
    console.log('🔍 Making API call to:', `/auth/get-authenticated-user/${user.uid}`);
    AxiosInstance.get(`/auth/get-authenticated-user/${user.uid}`)
      .then(async (response) => {
        console.log('🔍 API response for user info:', response);
        console.log('🔍 Response data:', response.data);
        
        // Check if user has BAC matricule and verify subscription
        if (response.data && response.data.bac_matricule) {
          console.log('🔍 User has BAC matricule, verifying subscription with Progres API');
          try {
            const verifyResponse = await AxiosInstance.post('/auth/verify-subscription', {
              matricule: response.data.bac_matricule
            });
            console.log('✅ Progres verification response:', verifyResponse.data);
          } catch (verifyError) {
            console.log('⚠️ Progres verification failed (non-blocking):', verifyError);
            // Don't block login if verification fails
          }
        }
        
        const userClaims = await user.getIdTokenResult();
        console.log('🔍 User claims:', userClaims.claims);
        
        if (!userClaims.claims.role || (userClaims.claims.role && userClaims.claims.role === 'STUDENT')) {
          console.log('🔍 User is a student, checking personal information...');
          if (!response.data) {
            console.log('🔍 No personal information found, redirecting to personal-information page');
            history.push('/personal-information');
          } else {
            console.log('🔍 Personal information found, redirecting to home');
            history.push('/');
          }
        } else {
          console.log('🔍 User is not a student, role:', userClaims.claims.role);
        }
      })
      .catch((error) => {
        console.error('❌ Error fetching user personal information:', error);
        console.error('❌ Error response:', error.response);
        setErrors('Failed Fetching User Personnal Informaiton');
      });
  };

  const inputValues = {
    email: { value: '', validate: { required: false, pattern: emailRegex } },
    password: { value: '', validate: { required: true } },
    identifier: { value: '', validate: { required: false } },
  };

  const { formState, field, handleErrorsServer } = FormState(inputValues, () => {});

  // Debug form state changes
  useEffect(() => {
    console.log('Form state updated:', formState.inputValues);
  }, [formState.inputValues]);

  // Debug BAC matricule state changes
  useEffect(() => {
    console.log('BAC Matricule state updated:', bacMatricule);
  }, [bacMatricule]);

  useEffect(() => {
    console.log('Birth Date state updated:', birthDate);
  }, [birthDate]);

  const customSubmitHandler = (e) => {
    console.log('🚀 UNIFIED SUBMIT HANDLER TRIGGERED');
    console.log('🚀 Identifier value:', formState.inputValues.identifier);
    console.log('🚀 Password value:', formState.inputValues.password);
    
    e.preventDefault();
    
    // Get identifier and password from form
    const identifier = formState.inputValues.identifier?.trim();
    const password = formState.inputValues.password?.trim();
    
    // Validate input
    if (!identifier) {
      setErrors('Identifier (email or BAC matricule) is required');
      return;
    }
    
    if (!password) {
      setErrors('Password is required');
      return;
    }
    
    // Auto-detect identifier type
    const isEmail = emailRegex.test(identifier);
    const isBacMatricule = /^[A-Z0-9]{8,20}$/i.test(identifier);
    
    console.log('🔍 Identifier detection:', { isEmail, isBacMatricule, identifier });
    
    if (isEmail) {
      console.log('📧 Email detected, using Firebase login');
      // Use Firebase login
      auth.signInWithEmailAndPassword(identifier, password)
        .then(async (result) => {
          getUserPersonalInformation(result.user);
          setLoading(false);
        })
        .catch((error) => {
          switch (error.code) {
            case 'auth/user-not-found':
              setErrors(t('login.errors.user-not-found'));
              break;
            case 'auth/wrong-password':
              handleErrorsServer(['ERROR_PASSWORD_INVALID']);
              break;
            case 'auth/invalid-email':
              handleErrorsServer(['ERROR_EMAIL_PATTERN']);
              break;
            default:
              setErrors(t('login.errors.authentication-error'));
              break;
          }
          setLoading(false);
        });
    } else if (isBacMatricule) {
      console.log('🎓 BAC Matricule detected, using BAC matricule login');
      // Use BAC matricule login
      setBacMatricule(identifier);
      setBirthDate(password);
      // Call the BAC matricule login function
      const tempBacLogin = async () => {
        try {
          setLoading(true);
          const response = await AxiosInstance.post('/auth/login', {
            identifier: identifier,
            password: password
          });
          
          const userData = response.data?.data || response.data;
          
          if (userData.firebase_uid) {
            let firebaseEmail;
            let firebasePassword;
            
            if (userData.requiresFirebaseAuth && userData.email) {
              firebaseEmail = userData.email;
              firebasePassword = password;
            } else {
              firebaseEmail = `${userData.bac_matricule || identifier}@temp.opu.local`;
              firebasePassword = password;
            }
            
            const firebaseResult = await auth.signInWithEmailAndPassword(firebaseEmail, firebasePassword);
            setUser(firebaseResult.user);
            getUserPersonalInformation(firebaseResult.user);
          }
        } catch (error) {
          console.error('❌ BAC matricule login failed:', error);
          setErrors(error.response?.data?.message || t('login.errors.authentication-error'));
          setLoading(false);
        }
      };
      
      tempBacLogin();
    } else {
      console.log('❌ Invalid identifier format');
      setErrors('Invalid identifier format. Please enter a valid email or BAC matricule.');
    }
  };

  return {
    field,
    submitHandler: customSubmitHandler,
    firebaseProviderLogin,
    getUserPersonalInformation,
    loginInputs,
    setLoginInputs,
    setErrors,
    errors,
    loading,
    setLoading,
    authMethod,
    setAuthMethod,
    bacMatriculeLogin,
    firebaseLogin,
    // BAC matricule specific state
    bacMatricule,
    setBacMatricule,
    birthDate,
    setBirthDate,
  };
};

export default useForm;
